<script setup lang="ts">
import { CmsEventTypes } from '~/composables/useCmsEventTracking';
import LessonPlayer from '~/components/LessonPlayer/index.vue';
import useWatchTime from '~/composables/useWatchTime';
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { Rating } from "~/enums/rating.enums";
import { ActivityEvents } from '~/enums/tracking/tracking-events.enums';

const route = useRoute();
const { getLesson, course, getChapterByActivity, showFeedbackForm } = useCourseState();
const { dispatchCmsEvent } = useCmsEventTracking();
const { submit } = useLessonRatingStore();
const { dispatchMessage } = useSystemMessages();
const { dispatchTrackingEvent, virtualPageView } = useTracking();

const dispatchedCompleteEvent = ref(false);
const isEnded = ref(false);
const showRating = ref(false);
const lessonPlayerInstance = ref<InstanceType<typeof LessonPlayer> | null>(null);

const lessonId = computed(() => route.params.lessonId as string);
const lesson = computed(() => getLesson(lessonId.value));
const chapter = computed(() => getChapterByActivity(lessonId.value));
const vimeoId = computed(() => {
  // Get the vimeo id from the lesson video url
  const url = lesson.value?.video?.source;
  if (!url) {
    return null;
  }

  const patternNewFormat = /vimeo\.com\/(?:video\/)?(\d+)\/([a-z0-9]+)/;
  const patternOldFormat = /vimeo\.com\/(?:video\/)?(\d+)/;

  const matchNewFormat = url.match(patternNewFormat);

  if (matchNewFormat) {
    return matchNewFormat.slice(1).join('/');
  }

  const matchOldFormat = url.match(patternOldFormat);

  return matchOldFormat ? matchOldFormat[1] : null;
});

const watchTime = { ...useWatchTime(course.value?.id, getChapterByActivity(lessonId.value)?.id, lesson.value?.chapterContentId) };

const onCompleted = () => {
  markLessonComplete();

  if (lesson.value?.rating === null) {
    showRating.value = true;
  }
};

const onEnded = () => {
  if (document.fullscreenElement) {
    document.exitFullscreen().finally(() => {
      isEnded.value = true;
    });
  } else {
    isEnded.value = true;
  }
};

virtualPageView('course', 'lesson');

const submitRating = async (like: Rating.Like | Rating.Dislike | Rating.Dismiss) => {
  if (!course.value || !lesson.value) {
    return;
  }

  let rating = {
    [Rating.Like]: 1,
    [Rating.Dislike]: -1,
    [Rating.Dismiss]: 0,
  }[like];

  if (like !== Rating.Dismiss) {
    dispatchMessage('Success!', 'success', 'Thank you for your feedback!');
  }

  showRating.value = false;

  lesson.value.rating = rating;
  await submit(course.value.id, Number(lessonId.value), rating);

  dispatchTrackingEvent(ActivityEvents.LessonFeedbackGiven, {
    lesson_id: lesson.value.id,
    course_id: course.value.id,
    rating,
  });
};

const onRewatch = () => {
  isEnded.value = false;
  lessonPlayerInstance.value?.restart();
};

const markLessonComplete = () => {
  sendTrackingData(ActivityEvents.LessonCompleted);

  if (dispatchedCompleteEvent.value || lesson.value?.completed) {
    return;
  }

  dispatchCmsEvent(CmsEventTypes.LessonCompleted, lessonId.value);
  dispatchedCompleteEvent.value = true;
};

onBeforeUnmount(() => {
  watchTime.destroy();
  watchTime.submit().catch(console.error);
});

definePageMeta({
  layout: 'empty',
  name: 'lesson',
  middleware: [
    'course-activity-access-middleware',
  ],
});

useHead({
  title: `${lesson.value?.title || 'Lesson'}`,
});

const activityInformationNeeded = computed(() => {
  return !course.value?.activityInformationAccepted && (course.value?.cmeCredits && course.value?.cmeCredits > 0);
});

watch(() => activityInformationNeeded.value, (value, oldValue) => {
  if (oldValue && !value) {
    sendLessonStarted();
  }
});

const sendTrackingData = (event: ActivityEvents) => {
  if (!lesson.value || !chapter.value || !course.value) {
    return;
  }

  dispatchTrackingEvent(event, {
    lesson_name: lesson.value.title,
    lesson_id: lesson.value.id,
    chapter_name: chapter.value.title,
    course_name: course.value.courseTitle,
    course_id: course.value.id,
    duration: lesson.value.duration ? lesson.value.duration / 60 : 0,
  });
};

const sendLessonStarted = () => {
  if (activityInformationNeeded.value) {
    return;
  }

  if (!lesson.value || !chapter.value || !course.value) {
    return;
  }

  dispatchCmsEvent(CmsEventTypes.LessonStarted, lessonId.value);
  sendTrackingData(ActivityEvents.LessonStarted);
};

onMounted(() => {
  sendLessonStarted();
});
</script>

<template>
  <article class="lg:p-6 flex flex-col lg:overflow-y-scroll lg:max-h-[calc(100vh-72px)]">
    <LessonEndCard
      v-if="isEnded"
      @rewatch="onRewatch"
    />
    <LessonPlayer
      v-else-if="vimeoId && !activityInformationNeeded && !showFeedbackForm"
      ref="lessonPlayerInstance"
      :vimeo-id="vimeoId"
      @ended="onEnded"
      @completed="onCompleted"
      @play="watchTime.play"
      @pause="watchTime.pause"
    />

    <header class="lg:flex hidden md:flex-row flex-col lg:gap-3 gap-6 justify-between lg:px-0 px-4 lg:pt-6 pt-4 lg:pb-0 pb-4">
      <div class="lg:text-20 text-16 font-bold text-neutral-100 flex items-center">
        {{lesson?.title}}
      </div>

      <div
        v-if="showRating"
        class="flex items-center gap-2 text-nowrap h-[23px]"
      >
        <span class="text-14 font-bold mr-2">
          Was this lesson helpful?
        </span>

        <Button
          class="p-2 group"
          :severity="lesson?.rating === 1 ? 'primary' : 'secondary'"
          @click="submitRating(Rating.Like)"
        >
          <FontAwesomeIcon
            icon="fa-thumbs-up"
            class="text-18"
          />
        </Button>

        <Button
          class="p-2"
          :severity="lesson?.rating === -1 ? 'primary' : 'secondary'"
          @click="submitRating(Rating.Dislike)"
        >
          <FontAwesomeIcon
            icon="fa-thumbs-down"
            class="text-18"
          />
        </Button>
      </div>
    </header>
  </article>
</template>